// ── Internationalization (i18n) module ────────────────────────────────
// Provides translation support with locale JSON files and DOM binding.
// Arabic and Hebrew are RTL languages — direction is set dynamically.

import { api_v1_locale_path } from './api-routes';

const RTL_LANGUAGES = ['ar', 'he'];

const SUPPORTED_LANGS = ["en", "uk", "pl", "de", "es", "fr", "pt", "it", "ar", "tr", "he", "nl", "cs", "ro", "hr", "bg", "sk", "sv", "el", "hu"];
const DEFAULT_LANG = "en";
// Language detected from URL path, no localStorage persistence

let currentLang = DEFAULT_LANG;
let translations = {};

/**
 * Set document direction (RTL/LTR) based on the active locale.
 */
function setDirection(locale: string) {
  document.documentElement.dir = RTL_LANGUAGES.includes(locale) ? 'rtl' : 'ltr';
}

/**
 * Substitute `{name}` placeholders. Exported so callers that fall back to a
 * hardcoded english string can interpolate it the same way a resolved
 * translation would be — a fallback carrying `{count}` used to reach the user
 * verbatim, e.g. the toast "Placed {count} heads".
 */
export function interpolate(
  template: string,
  params?: Record<string, string | number>
): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    params[k] !== undefined ? String(params[k]) : `{${k}}`
  );
}

/**
 * Resolve a dot-notation key against the translations object.
 * Returns the key itself if not found (graceful fallback).
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const parts = key.split(".");
  let value = translations;
  for (const part of parts) {
    if (value === null || value === undefined || typeof value !== "object") return key;
    value = (value as Record<string, any>)[part];
  }
  if (typeof value !== "string") return key;
  return interpolate(value, params);
}

/**
 * Load a locale JSON file and set it as active.
 */
// Keyed by language so a second caller for the same locale joins the request
// already in flight instead of starting its own. A rejected load is dropped
// from the map so a transient failure does not poison later attempts.
const inFlightLocales = new Map<string, Promise<void>>();

export async function loadLocale(lang: string) {
  if (!SUPPORTED_LANGS.includes(lang)) lang = DEFAULT_LANG;

  const pending = inFlightLocales.get(lang);
  if (pending) return pending;

  const request = (async () => {
    const url = api_v1_locale_path(lang);
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Failed to load locale: ${url}`);
    translations = await resp.json();
    currentLang = lang;
  })();

  inFlightLocales.set(lang, request);
  try {
    await request;
  } finally {
    inFlightLocales.delete(lang);
  }
}

/**
 * Get the current language code.
 */
export function getCurrentLanguage() {
  return currentLang;
}

/**
 * Read the locale directly from `<html lang>` (server-rendered) with an
 * English fallback. Prefer this over `getCurrentLanguage()` on code paths
 * that may run before `loadLocale()` has populated the module-local
 * `currentLang` (e.g. page-entry scripts on SSR-only pages).
 */
export function getDocumentLanguage(): string {
  return document.documentElement.lang || 'en';
}

/**
 * Switch language: load locale, persist preference, and re-apply DOM translations.
 */
export async function setLanguage(lang: string) {
  await loadLocale(lang);
  document.documentElement.lang = lang;
  setDirection(lang);
  applyTranslations();
}

/**
 * Scan the DOM for elements with data-i18n attributes and apply translations.
 *
 * Supported formats:
 *   data-i18n="toolbar.select"                → sets textContent
 *   data-i18n="[title]toolbar.selectTitle"     → sets title attribute
 *   data-i18n="[placeholder]sidebar.search"    → sets placeholder attribute
 *   data-i18n="toolbar.select;[title]toolbar.selectTitle" → multiple bindings
 */
export function applyTranslations() {
  document.querySelectorAll("[data-i18n]").forEach((el) => {
    const spec = el.getAttribute("data-i18n");
    const bindings = spec!.split(";");

    for (const binding of bindings) {
      const trimmed = binding.trim();
      const attrMatch = trimmed.match(/^\[([^\]]+)\](.+)$/);
      if (attrMatch) {
        const [, attr, key] = attrMatch;
        const val = t(key);
        if (val !== key) {
          if (attr === "innerHTML") {
            // Only allow innerHTML for keys that intentionally contain HTML markup
            // (e.g., keys with <strong>, <a>, <br> tags for rich text formatting).
            // All translation values are author-controlled, not user input.
            el.innerHTML = val;
          } else {
            el.setAttribute(attr, val);
          }
        }
      } else {
        const val = t(trimmed);
        if (val !== trimmed) {
          // If the element contains child nodes that are themselves translated
          // (data-i18n descendants), replacing textContent would wipe the
          // markup and leave literal tag text. In that case only swap the
          // leading text node(s) up to the first data-i18n child. Heuristic:
          // if there are data-i18n children, skip the outer replacement — SSR
          // already produced the correct fallback text, and children still
          // hydrate individually on the next pass.
          const hasI18nChildren = el.querySelector && el.querySelector("[data-i18n]");
          if (!hasI18nChildren) {
            el.textContent = val;
          }
        }
      }
    }
  });

  applyAttributeTranslations();

  // Update page title only on the planner SPA — non-planner pages (blog, brands,
  // landing, etc.) set their own server-rendered <title>, which must not be
  // overridden by the generic "pageTitle" planner translation.
  const isPlannerPage = document.body?.classList?.contains("planner-body")
    || document.querySelector?.("#plannerCanvas") !== null;
  if (isPlannerPage) {
    const titleKey = "pageTitle";
    const title = t(titleKey);
    if (title !== titleKey) document.title = title;
  }
}

/**
 * Detect the preferred language from URL path, URL param, or browser setting.
 */
export function detectLanguage() {
  // 1. URL path: /en/..., /uk/..., /hu/...
  const pathMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (pathMatch && SUPPORTED_LANGS.includes(pathMatch[1])) return pathMatch[1];

  // 2. URL parameter ?lang=en (legacy fallback)
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get("lang");
  if (urlLang && SUPPORTED_LANGS.includes(urlLang)) return urlLang;

  // 3. Browser language
  const browserLang = (navigator.language || "").slice(0, 2).toLowerCase();
  if (SUPPORTED_LANGS.includes(browserLang)) return browserLang;

  return DEFAULT_LANG;
}

/**
 * Observe DOM for new elements with data-i18n and translate them automatically.
 * This handles dynamically created UI (toolbar, sidebar, compass, AI features).
 */
let observer: unknown = null;
function startObserver() {
  if (observer || typeof MutationObserver === 'undefined') return;
  const attrSelector = ['data-i18n', ...ATTR_BINDINGS.map(([d]) => d)]
    .map((d) => `[${d}]`)
    .join(',');
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue; // Node.ELEMENT_NODE
        const el = node as Element;
        const hasDirectBinding =
          el.hasAttribute &&
          (el.hasAttribute('data-i18n') ||
            ATTR_BINDINGS.some(([d]) => el.hasAttribute(d)));
        if (hasDirectBinding) translateElement(el as HTMLElement);
        const children = el.querySelectorAll && el.querySelectorAll(attrSelector);
        if (children) children.forEach((child) => translateElement(child as HTMLElement));
      }
    }
  });
  // @ts-expect-error — TS strict
  observer.observe(document.body, { childList: true, subtree: true });
}

function translateElement(el: HTMLElement) {
  const spec = el.getAttribute('data-i18n');
  if (spec) {
    const bindings = spec.split(';');
    for (const binding of bindings) {
      const trimmed = binding.trim();
      const attrMatch = trimmed.match(/^\[([^\]]+)\](.+)$/);
      if (attrMatch) {
        const val = t(attrMatch[2]);
        if (val !== attrMatch[2]) el.setAttribute(attrMatch[1], val);
      } else {
        const val = t(trimmed);
        if (val !== trimmed) el.textContent = val;
      }
    }
  }
  translateAttributeBindings(el);
}

// Attribute-only i18n bindings (data-i18n-aria-label, data-i18n-title, ...).
// SSR renders the English fallback in the literal `aria-label` / `title`
// attribute; we swap it here once translations are available.
const ATTR_BINDINGS: Array<[string, string]> = [
  ['data-i18n-aria-label', 'aria-label'],
  ['data-i18n-title', 'title'],
  ['data-i18n-placeholder', 'placeholder'],
];

function translateAttributeBindings(el: Element) {
  for (const [dataAttr, targetAttr] of ATTR_BINDINGS) {
    const key = el.getAttribute(dataAttr);
    if (!key) continue;
    const val = t(key);
    if (val !== key) el.setAttribute(targetAttr, val);
  }
}

function applyAttributeTranslations() {
  const selector = ATTR_BINDINGS.map(([d]) => `[${d}]`).join(',');
  document.querySelectorAll(selector).forEach((el) => translateAttributeBindings(el));
}

/**
 * Initialize i18n: detect language, load locale, apply to DOM.
 * Call this once on page load.
 */
export async function initI18n() {
  const lang = detectLanguage();
  // Every entrypoint awaits this before wiring its page — the login/register
  // submit handlers, the dashboard, the marketplace, the feedback widget. A
  // failed /api/v1/locale request (5xx, dropped connection) used to reject out
  // of here and abort all of that. The server-rendered text is already on the
  // page; losing the dictionary only leaves JS-built strings as their keys.
  try {
    await loadLocale(lang);
  } catch (err) {
    console.error("i18n: locale failed to load, keeping server-rendered text:", err);
  }
  document.documentElement.lang = lang;
  setDirection(lang);
  applyTranslations();
  startObserver();
}
